/**
 * The backup lock on this device (decision 72; part 41b log, principles BP2, BP7, BP8, table E): what the device keeps
 * once the writer sets a backup passphrase. Never the passphrase (BP2): the key Argon2id derived from it (non-extractable,
 * with its KDF record), the 32-byte check value the passphrase check compares with, a random ID naming this setting (so
 * the backup log can tell which files were sealed under it: E4–E6), when it was set and last checked, and the day a check
 * was put off until (E17). Read field by field at the boundary: a record that can't be read is never taken for none (E7).
 */
import { readKdfRecord } from '@cockatrice/crypto';
import type { BackupKey } from '@cockatrice/crypto';
import { localDay } from '../count/day.ts';

/** E2, E8: the ending of a locked backup's file name; a plaintext one ends `.zip` (E1). */
export const LOCKED_ENDING = '.cockatrice-backup';

/** W1, E1, E2: the endings a backup's name may have. */
export type BackupEnding = '.zip' | typeof LOCKED_ENDING;

/** BP7, E15: days from setting the passphrase to its first check. */
export const FIRST_CHECK_DAYS = 30;

/** BP7, E15: days from one passed check to the next (decision 43's recovery-key rhythm). */
export const CHECK_DAYS = 180;

/** BP2: the check value's length. */
export const CHECK_BYTES = 32;

const DAY_MS = 86_400_000;
const LOCK_ID = /^[0-9a-f]{32}$/u;
const DAY = /^\d{4}-\d{2}-\d{2}$/u;

/** BP2, BP7, E2: the backup lock as the device keeps it. */
export interface BackupLock {
  /** E4–E6: 32 lowercase hex digits, random each time a passphrase is set or changed. */
  readonly id: string;
  readonly key: BackupKey;
  /** BP2: HKDF(K, `cockatrice/backup/check/v1`), {@link CHECK_BYTES} long. */
  readonly check: Uint8Array<ArrayBuffer>;
  readonly setAt: number;
  /** E15: when the passphrase was last checked right; {@link BackupLock.setAt} until the first check. */
  readonly checkedAt: number;
  /** E17: the local day a check was put off until, or null. */
  readonly later: string | null;
}

/** E1, E2, E7: the lock as the backup log holds it: none, one, or a record present that can't be read. */
export type LockState = { readonly kind: 'off' } | { readonly kind: 'on'; readonly lock: BackupLock } | { readonly kind: 'unreadable' };

/** The own fields of a plain object with exactly `keys`, or null. */
function fieldsOf(value: unknown, keys: readonly string[]): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === keys.length && keys.every((key) => fields.has(key)) ? fields : null;
}

/** A time: a whole number from 0. */
function isTime(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** E4–E6: whether `value` is a lock ID (32 lowercase hex digits). */
export function isLockId(value: unknown): value is string {
  return typeof value === 'string' && LOCK_ID.test(value);
}

/** BP2: a backup key read from a message or the log: its KDF record (table R) and a `CryptoKey`; null for anything else. */
export function readBackupKey(value: unknown): BackupKey | null {
  const fields = fieldsOf(value, ['kdf', 'key']);
  const kdf = readKdfRecord(fields?.get('kdf'));
  const key = fields?.get('key');
  return kdf !== null && key instanceof CryptoKey ? { kdf, key } : null;
}

/** BP2: a check value read from a message or the log, copied; null unless {@link CHECK_BYTES} bytes. */
export function readCheckValue(value: unknown): Uint8Array<ArrayBuffer> | null {
  return value instanceof Uint8Array && value.length === CHECK_BYTES ? value.slice() : null;
}

/** E7: the lock record `value` is, field by field, or null for anything else (never "off": the caller says unreadable). */
export function readBackupLock(value: unknown): BackupLock | null {
  const fields = fieldsOf(value, ['id', 'key', 'check', 'setAt', 'checkedAt', 'later']);
  const id = fields?.get('id');
  const key = readBackupKey(fields?.get('key'));
  const check = readCheckValue(fields?.get('check'));
  const setAt = fields?.get('setAt');
  const checkedAt = fields?.get('checkedAt');
  const later = fields?.get('later');
  if (!isLockId(id) || key === null || check === null || !isTime(setAt) || !isTime(checkedAt)) return null;
  if (later !== null && !(typeof later === 'string' && DAY.test(later))) return null;
  return { id, key, check, setAt, checkedAt, later };
}

/** E1, E2, E7: the lock state a stored value is: none is off, a readable lock is on, anything else unreadable. */
export function lockState(value: unknown): LockState {
  if (value === undefined) return { kind: 'off' };
  const lock = readBackupLock(value);
  return lock === null ? { kind: 'unreadable' } : { kind: 'on', lock };
}

/**
 * BP7, E15–E17, deep-fix r3 table K: whether the passphrase check is due at `now` (`zone` minutes ahead of UTC):
 * {@link FIRST_CHECK_DAYS} after it was set until a check passes on or after that day (K1, K2: one passed earlier, as a
 * writer making sure right after setting it, doesn't stand for it), then {@link CHECK_DAYS} after the last check (K3);
 * never before the local day it was put off until (K4, E17). Once due it stays due as time goes on (until checked or put
 * off).
 */
export function checkDue(lock: BackupLock, now: number, zone: number): boolean {
  const first = lock.setAt + FIRST_CHECK_DAYS * DAY_MS;
  const next = lock.checkedAt >= first ? lock.checkedAt + CHECK_DAYS * DAY_MS : first;
  if (now < next) return false;
  return lock.later === null || localDay(now, zone) >= lock.later;
}

/** E17: the local day after the one `now` falls on, `zone` minutes ahead of UTC. */
export function nextDay(now: number, zone: number): string {
  // `zone` is one fixed offset, so a whole day on from `now` is always the next local day.
  return localDay(now + DAY_MS, zone);
}
